import { Component, TemplateFunction } from '@ribajs/core'
import {
  ShopifyProductRecommendationsService,
  ShopifyProductService,
} from '../services/index.js'
import { TwSlideshowComponent } from '../modules/tailwind/components'

import type { ShopifyProduct } from '../types'
import type { JsxSbProductRecommendationsProps } from '../types/jsx'

import type { SlideshowSlide } from '../modules/tailwind/types/index.js'

interface Scope {
  /** The product id's you want to get the recommendations for */
  productIds?: number[] | string | number
  limit: number
  /** If set also the products with this handle are displayed, you can use this to show this products as recommendations */
  addProductHandles: string | string[]
  recommendations: ShopifyProduct[]
  slides: SlideshowSlide<ShopifyProduct>[]
}

/**
 * A component to get the product recommendations for each line item in the cart
 */
export class SbProductRecommendationsComponent extends Component {
  public static tagName = 'sb-product-recommendations'

  protected slideshow?: TwSlideshowComponent

  static get observedAttributes(): (keyof JsxSbProductRecommendationsProps)[] {
    return ['product-ids', 'limit', 'add-product-handles']
  }

  protected requiredAttributes(): string[] {
    return []
  }

  public scope: Scope = {
    productIds: undefined,
    limit: 4,
    addProductHandles: [],
    recommendations: [],
    slides: new Array<SlideshowSlide<ShopifyProduct>>(),
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbProductRecommendationsComponent.observedAttributes)
  }

  protected async getProducts(productHandles: string[]) {
    try {
      const products = await ShopifyProductService.get(productHandles)
      return products
    } catch (error) {
      console.error(error)
      return []
    }
  }

  protected addSlides(items: ShopifyProduct[]) {
    const slides = new Array<SlideshowSlide<ShopifyProduct>>()
    for (let i = 0; i < items.length; i++) {
      const recommendation = items[i]
      slides.push({
        active: false,
        index: i,
        data: recommendation,
      })
    }
    this.scope.slides = slides
  }

  protected updateSlides() {
    this.slideshow?.updateItems()
  }

  protected async getRecommendations(productIds: number[], reduceLimit = 0) {
    const limit = this.scope.limit + reduceLimit
    if (limit <= 0) {
      return []
    }

    try {
      const recommendations = await ShopifyProductRecommendationsService.get(
        productIds,
        limit,
      )
      return recommendations
    } catch (error) {
      console.error(error)
      return []
    }
  }

  protected splitProductIds(productIds: string) {
    const ids = productIds.split(',')
    return ids.map(id => Number(id.trim()))
  }

  protected splitProductHandles(productHandles: string) {
    const handles = productHandles.split(',')
    return handles.map(handle => handle.trim())
  }

  protected parseProductIds(
    productIds: string | number | number[] | undefined,
  ) {
    if (!productIds) {
      productIds = []
    } else if (
      typeof productIds === 'string' ||
      typeof productIds === 'number'
    ) {
      productIds = this.splitProductIds(productIds.toString())
    }

    if (!Array.isArray(productIds)) {
      throw new Error("Product id's must be an Array of Numbers!")
    }

    return productIds
  }

  protected parseProductHandles(productHandles: string | string[] | undefined) {
    if (!productHandles) {
      productHandles = []
    } else if (
      typeof productHandles === 'string' ||
      typeof productHandles === 'number'
    ) {
      productHandles = this.splitProductHandles(productHandles)
    }

    if (!Array.isArray(productHandles)) {
      throw new Error('Product handles must be an Array of String!')
    }

    return productHandles
  }

  protected async beforeBind(): Promise<any> {
    this.scope.productIds = this.parseProductIds(this.scope.productIds)
    this.scope.addProductHandles = this.parseProductHandles(
      this.scope.addProductHandles,
    )

    const products: ShopifyProduct[] = []
    const recommendations: ShopifyProduct[] = []

    try {
      products.push(...(await this.getProducts(this.scope.addProductHandles)))
      recommendations.push(
        ...(await this.getRecommendations(
          this.scope.productIds,
          products.length,
        )),
      )
    } catch (error) {
      this.error(error)
      this.innerHTML = `Error: ${(error as Error).message || error}`
      this.throw(error)
    }

    const all = [...products, ...recommendations].slice(0, this.scope.limit)
    this.scope.recommendations.push(...all)

    this.addSlides(this.scope.recommendations)

    await super.beforeBind()
  }

  protected async afterBind() {
    await super.afterBind()
    this.slideshow =
      this.querySelector(TwSlideshowComponent.tagName) || undefined

    if (!this.slideshow) {
      console.error('Slideshow not found!')
    }

    this.updateSlides()
    this.classList.add(`${SbProductRecommendationsComponent.tagName}-ready`)
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
